var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { noopFunction, useClasses, useProps } from '@hawkins/helpers';
import { useCombineRefs, useEffectPostInitialRender } from '../hooks';
import { COLLAPSE_ANIMATION_DURATION, useCollapseStyles } from './collapse.styles';
var displayName = 'Collapse';
/**
 * A transition component that collapses/expands its children.
 *
 * It is NOT recommended to add styling to the `Collapse` component
 * (specifically, padding and borders can interfere with its functionality).
 * If you need custom styling, apply it to the children of `Collapse` or wrap `Collapse` inside a styled element.
 */
export var Collapse = forwardRef(function (_a, refProp) {
    var _b;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _c = useProps(displayName, props), classesProp = _c.classes, className = _c.className, _d = _c.component, Component = _d === void 0 ? 'div' : _d, _e = _c.disableAnimation, disableAnimation = _e === void 0 ? false : _e, inProp = _c.in, _f = _c.onEnter, onEnter = _f === void 0 ? noopFunction : _f, _g = _c.onEntered, onEntered = _g === void 0 ? noopFunction : _g, _h = _c.onExit, onExit = _h === void 0 ? noopFunction : _h, _j = _c.onExited, onExited = _j === void 0 ? noopFunction : _j, restProps = __rest(_c, ["classes", "className", "component", "disableAnimation", "in", "onEnter", "onEntered", "onExit", "onExited"]);
    // Refs
    var internalRef = useRef(null);
    var ref = useCombineRefs([internalRef, refProp]);
    useEffect(function () {
        // If the animation is disabled, ignore this
        if (disableAnimation)
            return;
        // Check for the `style` property in case the user has customized the root element
        // and is not passing the ref as expected.
        if (!internalRef.current || !internalRef.current.style)
            return;
        if (inProp) {
            var _a = internalRef.current, clientHeight = _a.clientHeight, offsetHeight = _a.offsetHeight, scrollHeight = _a.scrollHeight;
            // `scrollHeight` doesn't include border width.
            // `offsetHeight - clientHeight` gives us the border width.
            // This logic accounts for padding and borders in case the user is styling this component,
            // but the best case scenario is if NO horizontal padding/borders/scrollbars are involved.
            // Users should instead style the children of this component or a wrapper of this component.
            var expandedHeight = scrollHeight + (offsetHeight - clientHeight);
            var initialHeight = internalRef.current.getBoundingClientRect().height;
            // If the element starts out expanded, it will have an `initialHeight` greater than 0.
            // In this case, we want to keep `initialHeight` as its height,
            // because our `expandedHeight` calculation may be off by < 1 pixel
            // due to `offsetHeight`, `clientHeight`, and `scrollHeight` always being integers.
            // Changing from `initialHeight` to `expandedHeight` could cause a small yet noticeable layout shift of < 1 pixel.
            // We only want to use `expandedHeight` if we're going from collapsed (i.e., a 0-pixel height) to expanded.
            internalRef.current.style.height = "".concat(initialHeight || expandedHeight, "px");
            var timeoutId_1 = setTimeout(function () {
                var _a;
                if ((_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.style) {
                    // This will allow the root element to automatically resize if the height of its content changes without
                    // impacting performance (for example, if we have nested Accordions, we do NOT want nested height animations!).
                    internalRef.current.style.height = 'auto';
                }
            }, COLLAPSE_ANIMATION_DURATION);
            return function () {
                clearTimeout(timeoutId_1);
            };
        }
        else {
            // We need to give the element an explicit height so the height can be animated.
            //
            // It's okay to execute this even when the element starts out collapsed,
            // as its `offsetHeight` in that case will be 0.
            internalRef.current.style.height = "".concat(internalRef.current.offsetHeight, "px");
            var timeoutId_2 = setTimeout(function () {
                var _a;
                if ((_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.style) {
                    internalRef.current.style.height = '0px';
                }
            }, 10);
            return function () {
                clearTimeout(timeoutId_2);
            };
        }
    }, [disableAnimation, inProp]);
    // Styles
    var baseClasses = useCollapseStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_b = {},
        _b[classes.rootDisabledAnimation] = disableAnimation,
        _b[classes.hidden] = !inProp,
        _b[classes.shown] = inProp,
        _b));
    // Wait for the CSS animation before firing callbacks
    useEffectPostInitialRender(function () {
        var startCallback = inProp ? onEnter : onExit;
        var endCallback = inProp ? onEntered : onExited;
        startCallback();
        var timeout = setTimeout(function () {
            endCallback();
        }, disableAnimation ? 0 : COLLAPSE_ANIMATION_DURATION);
        return function () {
            clearTimeout(timeout);
        };
    }, [inProp]);
    return (_jsx(Component, __assign({ className: rootClassName, ref: ref }, restProps, { children: children })));
});
Collapse.displayName = displayName;
